// src/components/groups/GroupForm.tsx

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    FormHelperText,
    Box,
    Chip,
    Alert,
    Typography,
    OutlinedInput,
} from '@mui/material';
import { Group, Student, CreateGroupDto } from '../../types';

export interface GroupFormRef {
    submit: () => void;
}

interface GroupFormProps {
    initialData?: Group | null;
    students: Student[];
    onSubmit: (data: CreateGroupDto) => void;
    loading?: boolean;
}

const GroupForm = forwardRef<GroupFormRef, GroupFormProps>(({
                                                                initialData,
                                                                students,
                                                                onSubmit,
                                                                loading = false,
                                                            }, ref) => {
    const [formData, setFormData] = useState<CreateGroupDto>({
        name: '',
        studentIds: [],
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    // Активные ученики (не в группах + ученики из текущей группы при редактировании)
    const getAvailableStudents = () => {
        if (initialData) {
            // При редактировании - показываем всех активных учеников + учеников из этой группы
            return students.filter(s =>
                s.isActive && (s.groupId === null || s.groupId === initialData.id)
            );
        }
        // При создании - только ученики без группы
        return students.filter(s => s.isActive && s.groupId === null);
    };

    const availableStudents = getAvailableStudents();

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                // Гарантируем что studentIds всегда массив
                studentIds: Array.isArray(initialData.studentIds) ? initialData.studentIds : [],
            });
        } else {
            setFormData({
                name: '',
                studentIds: [],
            });
            setErrors({});
        }
    }, [initialData]);

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSelectChange = (e: SelectChangeEvent<number[]>) => {
        const value = e.target.value as number[];
        setFormData(prev => ({ ...prev, studentIds: value || [] }));
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name || formData.name.trim().length < 2) {
            newErrors.name = 'Название группы должно содержать минимум 2 символа';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        console.log('GroupForm: handleSubmit called');
        if (validate()) {
            const cleanData: CreateGroupDto = {
                name: formData.name.trim(),
                studentIds: Array.isArray(formData.studentIds) ? formData.studentIds : [],
            };
            console.log('GroupForm: submitting data', cleanData);
            onSubmit(cleanData);
        } else {
            console.log('GroupForm: validation failed', errors);
        }
    };

    const getStudentName = (id: number) => {
        const student = students.find(s => s.id === id);
        return student ? student.fullName : 'Неизвестный ученик';
    };

    return (
        <Box sx={{ mt: 1 }}>
            {initialData && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Редактирование группы: {initialData.name}
                    <Chip
                        label={`${Array.isArray(initialData.studentIds) ? initialData.studentIds.length : 0} учеников`}
                        color="primary"
                        size="small"
                        sx={{ ml: 1 }}
                    />
                </Alert>
            )}

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        required
                        label="Название группы"
                        name="name"
                        value={formData.name}
                        onChange={handleTextChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder="Группа по математике"
                        disabled={loading}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                        <InputLabel>Ученики в группе</InputLabel>
                        <Select
                            multiple
                            name="studentIds"
                            value={formData.studentIds || []}
                            onChange={handleSelectChange}
                            input={<OutlinedInput label="Ученики в группе" />}
                            disabled={loading}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {(selected as number[]).map((id) => (
                                        <Chip
                                            key={id}
                                            label={getStudentName(id)}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            )}
                        >
                            {availableStudents.length === 0 ? (
                                <MenuItem disabled>
                                    <Typography variant="body2" color="text.secondary">
                                        Нет доступных учеников
                                    </Typography>
                                </MenuItem>
                            ) : (
                                availableStudents.map((student) => (
                                    <MenuItem key={student.id} value={student.id}>
                                        {student.fullName}
                                        {student.groupId === initialData?.id && (
                                            <Chip
                                                label="В группе"
                                                size="small"
                                                color="primary"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                        <FormHelperText>
                            {availableStudents.length === 0
                                ? 'Все ученики уже состоят в группах'
                                : `Доступно учеников: ${availableStudents.length}`}
                        </FormHelperText>
                    </FormControl>
                </Grid>

                {formData.studentIds && formData.studentIds.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary">
                            В группе {formData.studentIds.length} учеников
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
});

GroupForm.displayName = 'GroupForm';

export default GroupForm;